// Third-party
import Head from 'next/head';

// First-party
import { Slot, createPage } from '@lib/util/templateHelpers';
import { gameToLdJson } from '@lib/util/gameToLdJson';
import Meta from '@lib/components/head/Meta';


// Component lib
import shared from '@lib/components';

// Local
import gameApi from '@/apis/drupal/game';
import GameTileLayout from '@/layouts/GameTileLayout';

// Local components
import AllGamesBlock from '@/components/AllGamesBlock';

const {Box, Block, Title, RouterLink, DocumentTitle, Content} = shared;

export async function getServerSideProps () {
  const games = await gameApi.listNew(process.env.APP_CONFIG.DEFAULT_GAME_LIMIT);
  
  return {
    props: {
      games
    },
  };
}

export default createPage('NewPage', {}, function NewPage ({ className }, props) {
	const title = 'New IO Games';
	const description = 'Discover the latest io games on iogames.space. Explore the newest io games that have caught our attention, including recent hits like Baseball Bros, Hexanaut, and Deadshot.';
  return (
    <GameTileLayout className={className}>
      <DocumentTitle title='New IO Games' />

      <Meta
				title={title}
				description={description}
				openGraph={{
				title: title,
				description: description,
				type: 'website'
				}}
			>
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'WebSite',
              'name': process.env.APP_CONFIG.DEFAULT_TITLE,
              'url': process.env.APP_CONFIG.URL + '/new'
            }, null, 2)
          }}
        />
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Thing", 
                    "@id": process.env.APP_CONFIG.URL,
                    "name": "Games"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Thing", 
                    "@id":process.env.APP_CONFIG.URL + '/new',
                    "name": "New io Games"
                  }
                }
              ],
            }, null, 2)
          }}
        />

        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@type': 'ItemList',
              'itemListElement': props.games.map((game, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'url': process.env.APP_CONFIG.URL + game.path,
                'name': game.name
               // 'item': gameToLdJson(game)
              }))
            }, null, 2)
          }}
        />
      </Meta>

      <AllGamesBlock feature games={props.games} fetchMoreGames={gameApi.listNew}>
        <Slot name='title'>New io Games</Slot>
      </AllGamesBlock>
      {
        <Block>
          <Box className='IndexPage__BottomBox'>
            <Title>New IO Games</Title>
            <Content>
              <Title h2 size4>Discover the Latest Additions to the IO Gaming World</Title>

              <p>Welcome to our New Games section, where we showcase the freshest io games just released into the wild! At <RouterLink href="/">iogames.space</RouterLink>, we're constantly on the lookout for innovative titles that push the boundaries of browser gaming. Here you'll find the newest io games that have caught our attention, including recent hits like <RouterLink href="/baseball-bros">Baseball Bros</RouterLink>, <RouterLink href="/hexanautio">Hexanaut</RouterLink> and <RouterLink href="/deadshot-io">Deadshot</RouterLink>.</p>
             
              <Title h2 size4>Why Try New IO Games?</Title>
              <p>New io games often bring exciting innovations to the genre, introducing fresh mechanics, unique art styles, and creative twists on familiar concepts. By exploring these recent releases, you'll:</p>
              <ul>
                <li>Experience cutting-edge browser gaming technology</li>
                <li>Discover emerging trends before they become mainstream</li>
                <li>Support independent game developers early in their journey</li>
                <li>Find hidden gems before they explode in popularity</li>
                <li>Enjoy being among the first to master new gameplay mechanics</li>
              </ul>

              <Title h2 size4>Our Selection Process</Title>
              <p>Not every new io game makes it to our collection. We carefully evaluate each title based on gameplay quality, innovation, performance, and potential for long-term appeal. The games featured here represent what we believe are the most promising new entries in the io gaming world.</p>
              
              <Title h2 size4>Recently Added Online Games</Title>
              <p>Our newest additions include exciting titles like <RouterLink href="/baseball-bros">Baseball Bros</RouterLink>, which brings competitive sports game action to your browser. And don't miss <RouterLink href="/deadshot-io">Deadshot</RouterLink> for precision-based combat or <RouterLink href="/hexanautio">Hexanaut</RouterLink> for strategic territory control in a hexagonal universe.</p>
              <p>We update this section regularly, so bookmark us and check back often to discover the next big thing in online gaming. Found a new io game that we haven't listed yet? <RouterLink href="/contact-us">Let us know</RouterLink>, and we might add it to our collection!</p>
             
              
            </Content>
          </Box>
        </Block>
      }
    </GameTileLayout>
  );
});
